import { useEffect, useState } from "react";
import { CheckCircle2, Loader2, Video, X, XCircle } from "lucide-react";
import toast from "react-hot-toast";
import { Button } from "@/components/ui/Button";
import { PageLoader } from "@/components/brand/PageLoader";
import { Textarea } from "@/components/ui/Textarea";
import { supabase } from "@/lib/supabase";

type ModerationStatus = "pending" | "approved" | "rejected" | "auto_approved";

interface ClipRow {
  id: string;
  uploader_id: string;
  storage_path: string;
  caption: string | null;
  moderation_status: ModerationStatus;
  rekognition_confidence: number | null;
  rekognition_labels: Record<string, unknown> | null;
  review_notes: string | null;
  created_at: string;
  uploader_name: string | null;
}

/**
 * Admin → Content queue. Clips held in the 50-80% inappropriate-
 * content band by the (future) Rekognition pipeline queue here for
 * manual review. Approve publishes immediately; reject removes.
 * Until the pipeline is wired, the queue is populated by the mobile
 * app inserting rows with moderation_status='pending'.
 */
export function AdminContent() {
  const [rows, setRows] = useState<ClipRow[] | null>(null);
  const [status, setStatus] = useState<ModerationStatus | "all">("pending");
  const [activeId, setActiveId] = useState<string | null>(null);
  const [decision, setDecision] = useState<"approved" | "rejected">("approved");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  async function load() {
    let q = supabase
      .from("media_clips")
      .select(
        "id, uploader_id, storage_path, caption, moderation_status, rekognition_confidence, rekognition_labels, review_notes, created_at, uploader:profiles!media_clips_uploader_profile_fkey(display_name)"
      )
      .order("created_at", { ascending: false })
      .limit(200);
    if (status !== "all") q = q.eq("moderation_status", status);
    const { data, error } = await q;
    if (error) {
      toast.error(error.message);
      setRows([]);
      return;
    }
    setRows(
      ((data ?? []) as unknown as Array<
        Omit<ClipRow, "uploader_name"> & {
          uploader: { display_name: string | null } | null;
        }
      >).map((r) => ({
        ...r,
        uploader_name: r.uploader?.display_name ?? null,
      }))
    );
  }

  useEffect(() => {
    setRows(null);
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  async function submit() {
    if (!activeId) return;
    setSubmitting(true);
    const { error } = await supabase
      .from("media_clips")
      .update({
        moderation_status: decision,
        review_notes: notes.trim() || null,
        reviewed_at: new Date().toISOString(),
      })
      .eq("id", activeId);
    if (error) {
      setSubmitting(false);
      return toast.error(error.message);
    }
    await supabase.rpc("log_audit", {
      action: `media_clip_${decision}`,
      entity_type: "media_clip",
      entity_id: activeId,
      details: { review_notes: notes.trim() || null },
    });
    toast.success(decision === "approved" ? "Approved" : "Rejected");
    setActiveId(null);
    setNotes("");
    setDecision("approved");
    setSubmitting(false);
    load();
  }

  return (
    <div>
      <header className="mb-6">
        <p className="mb-1.5 text-xs font-bold uppercase tracking-wider text-volt">
          Admin · Content queue
        </p>
        <h1 className="text-3xl font-semibold tracking-tight">Content queue</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Clips that AWS Rekognition flagged in the 50–80% confidence
          band land here for manual review. Approve publishes to Reels
          immediately; reject removes and notifies the uploader.
        </p>
      </header>

      <StatusTabs value={status} onChange={setStatus} />

      {rows === null ? (
        <PageLoader />
      ) : rows.length === 0 ? (
        <EmptyState />
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {rows.map((r) => (
            <ClipCard key={r.id} clip={r} onReview={() => setActiveId(r.id)} />
          ))}
        </div>
      )}

      {activeId && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
          onClick={(e) => e.target === e.currentTarget && setActiveId(null)}
        >
          <div className="w-full max-w-lg rounded-2xl border border-border bg-card p-6">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold">Review clip</h2>
              <button
                type="button"
                onClick={() => setActiveId(null)}
                className="text-muted-foreground hover:text-foreground"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="mb-4">
              <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-muted-foreground">
                Decision
              </label>
              <div className="flex gap-2">
                {(
                  [
                    { v: "approved", label: "Approve", icon: <CheckCircle2 className="h-3 w-3" /> },
                    { v: "rejected", label: "Reject", icon: <XCircle className="h-3 w-3" /> },
                  ] as const
                ).map((o) => (
                  <button
                    key={o.v}
                    type="button"
                    onClick={() => setDecision(o.v)}
                    className={
                      "inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-medium transition-colors " +
                      (decision === o.v
                        ? "bg-volt text-primary-foreground"
                        : "bg-secondary text-muted-foreground hover:bg-volt-subtle hover:text-foreground")
                    }
                  >
                    {o.icon}
                    {o.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="mb-4">
              <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-muted-foreground">
                Notes (optional)
              </label>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                placeholder="Shown to the uploader on rejection…"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setActiveId(null)}>
                Cancel
              </Button>
              <Button onClick={submit} disabled={submitting}>
                {submitting ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : (
                  <CheckCircle2 className="h-3 w-3" />
                )}
                Submit
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ClipCard({
  clip,
  onReview,
}: {
  clip: ClipRow;
  onReview: () => void;
}) {
  // storage_path is a Supabase Storage object path; we leave rendering
  // to whatever <video> tag we show once the storage bucket is
  // configured. For now, show a placeholder frame + metadata.
  return (
    <article className="flex flex-col overflow-hidden rounded-2xl border border-border bg-card">
      <div className="flex aspect-[9/16] items-center justify-center bg-[#0d1117] text-muted-foreground">
        <Video className="h-8 w-8 opacity-40" />
      </div>
      <div className="flex flex-1 flex-col gap-2 p-4">
        <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
          {clip.uploader_name ?? "Unknown"}
        </p>
        {clip.caption && (
          <p className="line-clamp-2 text-sm">{clip.caption}</p>
        )}
        <div className="mt-auto flex items-center justify-between gap-2 pt-3">
          <span className="text-[10px] text-muted-foreground">
            {clip.rekognition_confidence != null ? (
              <>AI confidence {(clip.rekognition_confidence * 100).toFixed(0)}%</>
            ) : (
              "No AI score"
            )}
          </span>
          {clip.moderation_status === "pending" ? (
            <Button size="sm" onClick={onReview}>
              Review
            </Button>
          ) : (
            <StatusPill status={clip.moderation_status} />
          )}
        </div>
      </div>
    </article>
  );
}

function StatusTabs({
  value,
  onChange,
}: {
  value: ModerationStatus | "all";
  onChange: (v: ModerationStatus | "all") => void;
}) {
  const options: Array<{ label: string; value: ModerationStatus | "all" }> = [
    { label: "Pending", value: "pending" },
    { label: "Approved", value: "approved" },
    { label: "Rejected", value: "rejected" },
    { label: "All", value: "all" },
  ];
  return (
    <div className="mb-4 flex flex-wrap gap-2">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          onClick={() => onChange(o.value)}
          className={
            "rounded-full px-3 py-1 text-xs font-medium transition-colors " +
            (value === o.value
              ? "bg-volt text-primary-foreground"
              : "bg-secondary text-muted-foreground hover:bg-volt-subtle hover:text-foreground")
          }
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function StatusPill({ status }: { status: ModerationStatus }) {
  if (status === "approved" || status === "auto_approved") {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-volt/15 px-2 py-0.5 text-[10px] font-bold text-volt">
        <CheckCircle2 className="h-2.5 w-2.5" />{" "}
        {status === "auto_approved" ? "Auto-approved" : "Approved"}
      </span>
    );
  }
  if (status === "rejected") {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-destructive/15 px-2 py-0.5 text-[10px] font-bold text-destructive">
        <XCircle className="h-2.5 w-2.5" /> Rejected
      </span>
    );
  }
  return null;
}

function EmptyState() {
  return (
    <div className="rounded-2xl border border-dashed border-border bg-card/40 p-10 text-center">
      <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-volt-subtle text-volt">
        <Video className="h-5 w-5" />
      </div>
      <p className="text-sm font-semibold">Nothing to review</p>
      <p className="mt-1 text-xs text-muted-foreground">
        Clips uploaded from the mobile app show up here when they need
        manual moderation.
      </p>
    </div>
  );
}
